import { ImageOff, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';

import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';

type ProductImageProps = {
  src: string | null;
  alt: string;
  size?: 'sm' | 'md';
  className?: string;
};

export function ProductImage({
  src,
  alt,
  size = 'sm',
  className,
}: ProductImageProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const sizeClasses = {
    sm: 'size-8',
    md: 'size-12',
  };

  if (!src || hasError) {
    return (
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-md bg-layout-subtle text-layout-foreground-secondary',
          sizeClasses[size],
          className,
        )}
      >
        <ImageOff className='size-4' />
      </div>
    );
  }

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <button
        className={cn(
          'shrink-0 cursor-pointer overflow-hidden rounded-md border border-border-default bg-white transition-all hover:ring-2 hover:ring-primary-default hover:ring-offset-2 focus:outline-hidden focus:ring-2 focus:ring-primary-default focus:ring-offset-2',
          sizeClasses[size],
          className,
        )}
        onClick={() => setIsOpen(true)}
        type='button'
      >
        <img
          alt={alt}
          className='size-full object-cover'
          onError={() => setHasError(true)}
          src={src}
        />
      </button>
      <DialogPortal>
        <DialogOverlay className='backdrop-blur-xs' />
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <div className='relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-white shadow-2xl'>
            <DialogClose className='absolute top-3 right-3 z-10 inline-flex size-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 focus:outline-hidden focus:ring-2 focus:ring-white'>
              <X className='size-5' />
              <span className='sr-only'>Close</span>
            </DialogClose>
            <img
              alt={alt}
              className='max-h-[85vh] max-w-[85vw] object-contain'
              onError={() => setHasError(true)}
              src={src}
            />
            <div className='absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/60 to-transparent p-4'>
              <p className='text-center font-medium text-white'>{alt}</p>
            </div>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
